











































































































































































































import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';

import Card from '@/components/UI/Card.vue';
import Toast from '@/mixins/toast';

import MmHintedButton from '../../components/MmHintedButton.vue';
import MmFileUpload from '../../components/MmFileUpload.vue';
import { createCancelSource } from '../../api/mmHttp';
import { createPaginationState } from '../../api/pagination';
import { isCanceled, MmError, toMmError } from '../../core/errors';
import {
	activateZoningModelVersion,
	assignZoningModel,
	deactivateZoningModelVersion,
	dryRunZoningModelUpload,
	fetchZoningAssignments,
	fetchZoningCompetitors,
	fetchZoningModelLines,
	uploadZoningModel,
} from '../../api/zoningModels';
import {
	MmZoningAssignment,
	MmZoningCompetitor,
	MmZoningModelLine,
	MmZoningModelVersion,
	MmZoningUploadDryRunReport,
} from '../../types/zoningModels';

const KEY = 'priceMonitoring.mobileMonitoring.zoningModels.';

@Component({
	components: {
		Card,
		MmHintedButton,
		MmFileUpload,
	},
})
export default class ZoningModelsSection extends Mixins(Toast) {
	@Prop({ required: true }) readonly tradingId!: number;

	@Prop({ default: null }) readonly tradingName!: string | null;

	@Prop({ default: true }) readonly isActive!: boolean;

	KEY = KEY;

	lines: MmZoningModelLine[] = [];

	assignments: MmZoningAssignment[] = [];

	competitors: MmZoningCompetitor[] = [];

	linesLoading = false;

	assignmentsLoading = false;

	competitorsLoading = false;

	linesError: MmError | null = null;

	assignmentsError: MmError | null = null;

	pendingVersionId: string | null = null;

	uploadVisible = false;

	uploadFile: File | null = null;

	uploadFileError = '';

	uploadForm = {
		modelKey: '',
		title: '',
		evalMetric: 'delta_zone_accuracy',
		evalDeltaPp: null as number | null,
		evalN: null as number | null,
	};

	dryRunReport: MmZoningUploadDryRunReport | null = null;

	dryRunLoading = false;

	uploadLoading = false;

	assignVisible = false;

	assignForm = {
		competitorId: undefined as string | undefined,
		lineId: undefined as string | undefined,
	};

	assignLoading = false;

	private cancelSource: import('axios').CancelTokenSource | null = null;

	get disabled(): boolean {
		return this.tradingId === null || this.tradingId <= 0;
	}

	get disabledReason(): string {
		return this.disabled ? (this.$t(KEY + 'networkRequired') as string) : '';
	}

	get lineColumns(): any[] {
		return [
			{ title: this.$t(KEY + 'library.colKey') as string, dataIndex: 'modelKey', key: 'modelKey', width: 160 },
			{ title: this.$t(KEY + 'library.colTitle') as string, dataIndex: 'title', key: 'title' },
			{ title: this.$t(KEY + 'library.colActive') as string, key: 'active', scopedSlots: { customRender: 'active' } },
			{ title: this.$t(KEY + 'library.colVersions') as string, dataIndex: 'versions.length', key: 'versionsCount', width: 100, customRender: (text: any, record: MmZoningModelLine) => record.versions.length },
			{ title: this.$t(KEY + 'library.colAssignments') as string, dataIndex: 'assignmentsCount', key: 'assignmentsCount', width: 110 },
		];
	}

	get versionColumns(): any[] {
		return [
			{ title: this.$t(KEY + 'versions.colVersion') as string, dataIndex: 'version', key: 'version' },
			{ title: this.$t(KEY + 'versions.colSize') as string, key: 'verSize', width: 110, scopedSlots: { customRender: 'verSize' } },
			{ title: this.$t(KEY + 'versions.colSha') as string, key: 'verSha', width: 140, scopedSlots: { customRender: 'verSha' } },
			{ title: this.$t(KEY + 'versions.colActive') as string, key: 'verActive', width: 110, scopedSlots: { customRender: 'verActive' } },
			{ title: this.$t(KEY + 'versions.colActions') as string, key: 'verActions', width: 130, scopedSlots: { customRender: 'verActions' } },
		];
	}

	get assignmentColumns(): any[] {
		return [
			{ title: this.$t(KEY + 'assignments.colCompetitor') as string, dataIndex: 'competitorName', key: 'competitorName' },
			{ title: this.$t(KEY + 'assignments.colLine') as string, key: 'asgLine', scopedSlots: { customRender: 'asgLine' } },
			{ title: this.$t(KEY + 'assignments.colAssignedAt') as string, dataIndex: 'assignedAt', key: 'assignedAt', width: 200 },
		];
	}

	created(): void {
		this.loadAll();
	}

	beforeDestroy(): void {
		this.cancelPending();
	}

	@Watch('tradingId')
	onTradingIdChanged(): void {
		this.loadAll();
	}

	@Watch('isActive')
	onActiveChanged(active: boolean): void {
		if (active && this.lines.length === 0 && this.linesError === null && !this.linesLoading) {
			this.loadAll();
		}
	}

	formatBytes(bytes: number): string {
		if (!bytes || bytes <= 0) {
			return '0 КБ';
		}

		const kb = bytes / 1024;

		return kb >= 1024 ? (kb / 1024).toFixed(1) + ' МБ' : Math.round(kb) + ' КБ';
	}

	shortSha(sha: string): string {
		return sha && sha.length > 12 ? sha.slice(0, 12) + '…' : sha;
	}

	loadAll(): void {
		if (this.disabled || !this.isActive) {
			return;
		}

		this.cancelPending();
		const source = createCancelSource();

		this.cancelSource = source;
		this.loadLines(source);
		this.loadAssignments(source);
		this.loadCompetitors(source);
	}

	private loadLines(source: import('axios').CancelTokenSource): void {
		this.linesLoading = true;
		this.linesError = null;

		fetchZoningModelLines(createPaginationState(100), { cancelToken: source.token })
			.then((page) => {
				if (this.cancelSource !== source) {
					return;
				}

				this.lines = page.content;
			})
			.catch((error) => {
				if (isCanceled(error) || this.cancelSource !== source) {
					return;
				}

				this.lines = [];
				this.linesError = toMmError(error, 'zoning/models');
			})
			.finally(() => {
				if (this.cancelSource === source) {
					this.linesLoading = false;
				}
			});
	}

	private loadAssignments(source: import('axios').CancelTokenSource): void {
		this.assignmentsLoading = true;
		this.assignmentsError = null;

		fetchZoningAssignments(createPaginationState(100), { cancelToken: source.token })
			.then((page) => {
				if (this.cancelSource !== source) {
					return;
				}

				this.assignments = page.content;
			})
			.catch((error) => {
				if (isCanceled(error) || this.cancelSource !== source) {
					return;
				}

				this.assignments = [];
				this.assignmentsError = toMmError(error, 'zoning/assignments');
			})
			.finally(() => {
				if (this.cancelSource === source) {
					this.assignmentsLoading = false;
				}
			});
	}

	private loadCompetitors(source: import('axios').CancelTokenSource): void {
		this.competitorsLoading = true;

		fetchZoningCompetitors({ cancelToken: source.token })
			.then((items) => {
				if (this.cancelSource !== source) {
					return;
				}

				this.competitors = items;
			})
			.catch((error) => {
				if (isCanceled(error) || this.cancelSource !== source) {
					return;
				}

				this.competitors = [];
			})
			.finally(() => {
				if (this.cancelSource === source) {
					this.competitorsLoading = false;
				}
			});
	}

	openUpload(): void {
		this.uploadFile = null;
		this.uploadFileError = '';
		this.dryRunReport = null;
		this.uploadForm = {
			modelKey: '',
			title: '',
			evalMetric: 'delta_zone_accuracy',
			evalDeltaPp: null,
			evalN: null,
		};
		this.uploadVisible = true;
	}

	closeUpload(): void {
		this.uploadVisible = false;
	}

	handleUploadFile(file: File | null): void {
		this.uploadFile = file;
		this.uploadFileError = '';
		this.dryRunReport = null;
	}

	private buildUploadPayload() {
		return {
			modelKey: this.uploadForm.modelKey.trim(),
			title: this.uploadForm.title.trim(),
			evaluation: {
				metric: this.uploadForm.evalMetric.trim(),
				deltaPp: this.uploadForm.evalDeltaPp !== null ? this.uploadForm.evalDeltaPp : 0,
				ciLow: 0,
				ciHigh: 0,
				n: this.uploadForm.evalN !== null ? this.uploadForm.evalN : 0,
				reportLink: '',
			},
		};
	}

	private validateUpload(): boolean {
		if (this.uploadFile === null) {
			this.uploadFileError = this.$t(KEY + 'uploadModal.fileRequired') as string;

			return false;
		}

		if (this.uploadForm.modelKey.trim() === '') {
			this.showError(this.$t(KEY + 'uploadModal.modelKeyRequired') as string);

			return false;
		}

		return true;
	}

	runDryRun(): void {
		if (!this.validateUpload() || this.uploadFile === null) {
			return;
		}

		this.dryRunLoading = true;
		this.dryRunReport = null;

		dryRunZoningModelUpload(this.uploadFile, this.buildUploadPayload())
			.then((report) => {
				this.dryRunReport = report;
			})
			.catch((error) => {
				if (!isCanceled(error)) {
					this.showResponseError(error);
				}
			})
			.finally(() => {
				this.dryRunLoading = false;
			});
	}

	submitUpload(): void {
		if (!this.validateUpload() || this.uploadFile === null) {
			return;
		}

		this.uploadLoading = true;

		uploadZoningModel(this.uploadFile, this.buildUploadPayload())
			.then(() => {
				this.showInfo(this.$t(KEY + 'uploadModal.success') as string);
				this.uploadVisible = false;
				this.loadAll();
			})
			.catch((error) => {
				if (!isCanceled(error)) {
					this.showResponseError(error);
				}
			})
			.finally(() => {
				this.uploadLoading = false;
			});
	}

	activateVersion(version: MmZoningModelVersion): void {
		this.pendingVersionId = version.id;

		activateZoningModelVersion(version.id)
			.then(() => {
				this.showInfo(this.$t(KEY + 'versions.activated') as string);
				this.loadAll();
			})
			.catch((error) => {
				if (!isCanceled(error)) {
					this.showResponseError(error);
				}
			})
			.finally(() => {
				this.pendingVersionId = null;
			});
	}

	deactivateVersion(version: MmZoningModelVersion): void {
		this.pendingVersionId = version.id;

		deactivateZoningModelVersion(version.id)
			.then(() => {
				this.showInfo(this.$t(KEY + 'versions.deactivated') as string);
				this.loadAll();
			})
			.catch((error) => {
				if (!isCanceled(error)) {
					this.showResponseError(error);
				}
			})
			.finally(() => {
				this.pendingVersionId = null;
			});
	}

	openAssign(): void {
		this.assignForm = { competitorId: undefined, lineId: undefined };
		this.assignVisible = true;
	}

	closeAssign(): void {
		this.assignVisible = false;
	}

	submitAssign(): void {
		const competitorId = this.assignForm.competitorId;
		const lineId = this.assignForm.lineId;

		if (!competitorId || !lineId) {
			this.showError(this.$t(KEY + 'assignModal.required') as string);

			return;
		}

		this.assignLoading = true;

		assignZoningModel({ competitorId: competitorId, lineId: lineId })
			.then(() => {
				this.showInfo(this.$t(KEY + 'assignModal.success') as string);
				this.assignVisible = false;
				this.loadAll();
			})
			.catch((error) => {
				if (!isCanceled(error)) {
					this.showResponseError(error);
				}
			})
			.finally(() => {
				this.assignLoading = false;
			});
	}

	private cancelPending(): void {
		if (this.cancelSource !== null) {
			this.cancelSource.cancel();
			this.cancelSource = null;
		}
	}
}
